/**
 * GSAP animation orchestration.
 *
 * One init per scrollable section, each gated by prefersReducedMotion().
 * If the user prefers reduced motion, sections are revealed without movement (content still appears).
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin( ScrollTrigger );

/** True when the user has asked the OS to reduce motion. WCAG 2.1 AA gate. */
function prefersReducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/** Hero — staggered fade/rise on load. */
function initHero() {
	const hero = document.querySelector( '[data-animate="hero"]' );
	if ( ! hero ) return;
	const items = hero.querySelectorAll( '[data-animate-item]' );
	if ( ! items.length ) return;

	if ( prefersReducedMotion() ) {
		gsap.set( items, { autoAlpha: 1 } );
		return;
	}

	gsap.fromTo(
		items,
		{ autoAlpha: 0, y: 24 },
		{ autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.12 }
	);
}

/** Timeline — reveal each event as it scrolls into view. */
function initTimeline() {
	const events = document.querySelectorAll( '[data-animate="timeline-event"]' );
	if ( ! events.length ) return;

	if ( prefersReducedMotion() ) {
		gsap.set( events, { autoAlpha: 1 } );
		return;
	}

	events.forEach( ( event ) => {
		gsap.fromTo(
			event,
			{ autoAlpha: 0, y: 32 },
			{
				autoAlpha: 1,
				y: 0,
				duration: 0.6,
				ease: 'power2.out',
				scrollTrigger: {
					trigger: event,
					start: 'top 80%',
					toggleActions: 'play none none reverse',
				},
			}
		);
	} );
}

/** Impact stats — count up to value when in view. */
function initStatCounters() {
	const counters = document.querySelectorAll( '[data-animate="stat"]' );
	if ( ! counters.length ) return;

	counters.forEach( ( el ) => {
		const target = parseFloat( el.dataset.value || el.textContent );
		if ( Number.isNaN( target ) ) return;

		const suffix = el.dataset.suffix || '';
		const decimals = ( el.dataset.decimals && parseInt( el.dataset.decimals, 10 ) ) || 0;

		if ( prefersReducedMotion() ) {
			el.textContent = target.toFixed( decimals ) + suffix;
			return;
		}

		const obj = { value: 0 };
		gsap.to( obj, {
			value: target,
			duration: 1.4,
			ease: 'power2.out',
			scrollTrigger: { trigger: el, start: 'top 85%', once: true },
			onUpdate() {
				el.textContent = obj.value.toFixed( decimals ) + suffix;
			},
		} );
	} );
}

/** Generic on-scroll fade/rise — drop `data-animate="fade-up"` on any element. */
function initFadeUp() {
	const items = document.querySelectorAll( '[data-animate="fade-up"]' );
	if ( ! items.length ) return;

	if ( prefersReducedMotion() ) {
		gsap.set( items, { autoAlpha: 1 } );
		return;
	}

	items.forEach( ( el ) => {
		gsap.fromTo(
			el,
			{ autoAlpha: 0, y: 24 },
			{
				autoAlpha: 1,
				y: 0,
				duration: 0.6,
				ease: 'power2.out',
				scrollTrigger: { trigger: el, start: 'top 85%', once: true },
			}
		);
	} );
}

/** Public entry point. */
export default function initAnimations() {
	initHero();
	initTimeline();
	initStatCounters();
	initFadeUp();
}
